import {
  CanSpamHold,
  describeCanSpamHold,
} from "components/EmailEditor/EmailEditorWarnings/canSpamHold";
import { broadcastEmailIds } from "domain/helpers/broadcastEmailIds";

/**
 * A broadcast whose email is held for CAN-SPAM (its stored verdict is
 * "pending") would send nothing: every send waits 15 minutes at a time and is
 * dropped after 7 days (V2-H4). Review says so, and Send and Schedule refuse
 * it, as the server's BroadcastService.start does.
 */

/**
 * The first email the broadcast sends whose sends are held, or undefined:
 * its email, and each A/B variant's while the test is on
 * (domain/helpers/broadcastEmailIds), the ones the server's refusal reads. A
 * follow-up is never sent (F29).
 */
export const heldBroadcastEmailId = (
  broadcast: Pick<Broadcast, "emailId" | "abTest">,
  verdictOf: (
    id: IEmailContent["id"]
  ) => IEmailContent["canSpamCompliance"] | undefined
): IEmailContent["id"] | undefined =>
  broadcastEmailIds(broadcast).find((id) => verdictOf(id) === "pending");

/** Why Send or Schedule is refused, for the email that is held. */
export const broadcastHoldMessage = (name?: string) =>
  `Sending of this broadcast's email${
    name ? ` "${name}"` : ""
  } is on hold: it has no working unsubscribe link or no mailing address. ` +
  "Open the email, fix it and save it.";

/**
 * What Review says about the broadcast's email while it can still be sent
 * and its sends are held: why (describeCanSpamHold, read from the email's
 * html once the full email has loaded) and what to do. undefined otherwise.
 */
export const reviewCanSpamHold = ({
  sendable,
  verdict,
  html,
  plainText = false,
}: {
  sendable: boolean;
  verdict: IEmailContent["canSpamCompliance"] | undefined;
  /** undefined until the full email has loaded (the list's copy has none) */
  html: string | undefined;
  plainText?: boolean;
}): CanSpamHold | undefined => {
  if (!sendable || verdict !== "pending") return undefined;
  if (html === undefined) {
    return {
      headline:
        "Sending is on hold: this email has no working unsubscribe link or no mailing address.",
      reasons: ["Open the email to see why, fix it and save it."],
    };
  }
  const hold = describeCanSpamHold({ verdict, html, plainText });
  return (
    hold && {
      ...hold,
      reasons: [
        ...hold.reasons,
        "Open the email, fix it and save it: this broadcast cannot be sent until then.",
      ],
    }
  );
};
